/**
 * @description
 * React hook for managing real-time deployment log streaming via WebSocket.
 * Connects to the process-manager service and subscribes to logs for a specific client.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 11, 2025
 * @see admin/process-manager/src/services/LogStreamer.ts
 */

import { useEffect, useState, useRef } from 'react';

interface LogMessage {
  timestamp: string;
  level: 'stdout' | 'stderr' | 'info' | 'error' | 'warn';
  message: string;
}

export function useDeploymentLogs(clientId: string | null) {
  const [logs, setLogs] = useState<LogMessage[]>([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  useEffect(() => {
    if (!clientId) return;

    const connectWebSocket = () => {
      try {
        // Connect to process-manager WebSocket
        const ws = new WebSocket('ws://localhost:3001/ws');
        wsRef.current = ws;

        ws.onopen = () => {
          console.log('[useDeploymentLogs] WebSocket connected');
          setConnected(true);
          setError(null);
          reconnectAttemptsRef.current = 0;
          
          // Subscribe to logs for this client
          ws.send(JSON.stringify({
            type: 'subscribe',
            clientId
          }));
        };

        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            
            if (message.type === 'log' && message.clientId === clientId) {
              setLogs(prev => [...prev, {
                timestamp: message.timestamp,
                level: message.level,
                message: message.message
              }]);
            } else if (message.type === 'error') {
              setError(message.message);
            } else if (message.type === 'connected') {
              console.log('[useDeploymentLogs]', message.message);
            }
          } catch (err) {
            console.error('[useDeploymentLogs] Failed to parse message:', err);
          }
        };

        ws.onerror = (event) => {
          console.error('[useDeploymentLogs] WebSocket error:', event);
          setError('WebSocket connection error');
          setConnected(false);
        };

        ws.onclose = () => {
          console.log('[useDeploymentLogs] WebSocket disconnected');
          setConnected(false);
          
          // Attempt to reconnect with exponential backoff
          if (reconnectAttemptsRef.current < maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
            console.log(`[useDeploymentLogs] Reconnecting in ${delay}ms...`);
            
            reconnectTimeoutRef.current = setTimeout(() => {
              reconnectAttemptsRef.current++;
              connectWebSocket();
            }, delay);
          } else {
            setError('Failed to connect after multiple attempts');
          }
        };
      } catch (err) {
        console.error('[useDeploymentLogs] Failed to create WebSocket:', err);
        setError('Failed to create WebSocket connection');
      }
    };

    connectWebSocket();

    // Cleanup
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      
      if (wsRef.current) {
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'unsubscribe',
            clientId
          }));
        }
        wsRef.current.close();
      }
    };
  }, [clientId]);

  const clearLogs = () => setLogs([]);

  return { logs, connected, error, clearLogs };
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */